import React, { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Shield, ShieldCheck, Eye, Trash2, Loader2, UserCheck, UserX, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/services/api';

const ROLE_CONFIG = {
  super_admin: { label: 'Super Admin', color: 'bg-red-100 text-red-800', icon: ShieldCheck },
  admin: { label: 'Admin', color: 'bg-blue-100 text-blue-800', icon: Shield },
  viewer: { label: 'Viewer', color: 'bg-gray-100 text-gray-800', icon: Eye },
};

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-green-100 text-green-800' },
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  disabled: { label: 'Disabled', color: 'bg-red-100 text-red-800' },
};

const UsersPanel = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', name: '', role: 'admin' });

  const isSuperAdmin = currentUser?.role === 'super_admin';

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api/admin/users');
      if (response.data.success) setUsers(response.data.users);
    } catch { toast.error('Failed to load users'); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleInvite = async () => {
    if (!inviteData.email) { toast.error('Email is required'); return; }
    setIsSaving(true);
    try {
      const response = await api.post('/api/admin/users/invite', inviteData);
      if (response.data.success) {
        toast.success(response.data.message);
        setShowInviteModal(false);
        setInviteData({ email: '', name: '', role: 'admin' });
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    } finally { setIsSaving(false); }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const response = await api.put(`/api/admin/users/${userId}`, updates);
      if (response.data.success) {
        toast.success('User updated');
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!confirm(`Are you sure you want to delete ${email}? This cannot be undone.`)) return;
    try {
      const response = await api.delete(`/api/admin/users/${userId}`);
      if (response.data.success) {
        toast.success('User deleted');
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#039fff]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="users-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#001A47]">User Management</h2>
          <p className="text-gray-500">Invite and manage admin users, assign roles and permissions</p>
        </div>
        <Button onClick={() => setShowInviteModal(true)} data-testid="invite-user-btn">
          <Plus className="h-4 w-4 mr-2" />Invite User
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-6 text-center">
          <p className="text-3xl font-bold text-[#001A47]">{users.length}</p>
          <p className="text-sm text-gray-500">Total Users</p>
        </CardContent></Card>
        <Card><CardContent className="pt-6 text-center">
          <p className="text-3xl font-bold text-green-600">{users.filter(u => u.status === 'active').length}</p>
          <p className="text-sm text-gray-500">Active</p>
        </CardContent></Card>
        <Card><CardContent className="pt-6 text-center">
          <p className="text-3xl font-bold text-yellow-600">{users.filter(u => u.status === 'pending').length}</p>
          <p className="text-sm text-gray-500">Pending</p>
        </CardContent></Card>
        <Card><CardContent className="pt-6 text-center">
          <p className="text-3xl font-bold text-red-600">{users.filter(u => u.status === 'disabled').length}</p>
          <p className="text-sm text-gray-500">Disabled</p>
        </CardContent></Card>
      </div>

      {/* User List */}
      <div className="space-y-3">
        {users.map((user) => {
          const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.viewer;
          const statusConfig = STATUS_CONFIG[user.status] || STATUS_CONFIG.pending;
          const RoleIcon = roleConfig.icon;
          const isCurrentUser = user.user_id === currentUser?.user_id;

          return (
            <Card key={user.user_id} className={`${isCurrentUser ? 'border-[#039fff]' : ''}`} data-testid={`user-row-${user.user_id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.status === 'active' ? 'bg-[#001A47]' : 'bg-gray-300'}`}>
                      {user.picture ? (
                        <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                      ) : (
                        <RoleIcon className="h-5 w-5 text-white" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{user.name}</p>
                        {isCurrentUser && <Badge variant="outline" className="text-xs">You</Badge>}
                      </div>
                      <p className="text-sm text-gray-500">{user.email}</p>
                      <p className="text-xs text-gray-400">
                        {user.auth_provider === 'google' ? 'Google' : 'Email'} · Joined {new Date(user.created_at).toLocaleDateString()}
                        {user.invited_by && ` · Invited by ${user.invited_by}`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Badge className={roleConfig.color}>{roleConfig.label}</Badge>
                    <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                    
                    {isSuperAdmin && !isCurrentUser && (
                      <div className="flex items-center gap-1 ml-2">
                        {/* Role select */}
                        <Select value={user.role} onValueChange={(v) => handleUpdateUser(user.user_id, { role: v })}>
                          <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="super_admin">Super Admin</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="viewer">Viewer</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        {/* Toggle status */}
                        {user.status === 'active' ? (
                          <Button variant="ghost" size="sm" className="text-orange-500 h-8" onClick={() => handleUpdateUser(user.user_id, { status: 'disabled' })}>
                            <UserX className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" className="text-green-500 h-8" onClick={() => handleUpdateUser(user.user_id, { status: 'active' })}>
                            <UserCheck className="h-4 w-4" />
                          </Button>
                        )}
                        
                        <Button variant="ghost" size="sm" className="text-red-500 h-8" onClick={() => handleDeleteUser(user.user_id, user.email)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent aria-describedby={undefined} data-testid="invite-user-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#039fff]" />
              Invite Admin User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email Address *</Label>
              <Input type="email" value={inviteData.email} onChange={(e) => setInviteData(d => ({ ...d, email: e.target.value }))} placeholder="user@example.com" />
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={inviteData.name} onChange={(e) => setInviteData(d => ({ ...d, name: e.target.value }))} placeholder="Full name (optional)" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={inviteData.role} onValueChange={(v) => setInviteData(d => ({ ...d, role: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — Full access to all features</SelectItem>
                  <SelectItem value="viewer">Viewer — Read-only access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-gray-500">
              The invited user will be able to register at <strong>/admin</strong> using this email address.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteModal(false)}>Cancel</Button>
            <Button onClick={handleInvite} disabled={isSaving} data-testid="send-invite-btn">
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPanel;
